import { useCallback, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Icon, IconSprite } from '../ui/Icon'
import { useSession } from '../features/auth/session'
import { LoginScreen } from '../features/auth/LoginScreen'
import { PasswordScreen } from '../features/auth/PasswordScreen'
import { Sidebar } from './Sidebar'
import { PageHeader, PageSlotContext } from './PageHeader'
import { Toast } from './Toast'
import { currentModule } from './modules'
import { useOrders } from '../features/orders/api'
import { useUnreadCount } from '../features/notifications/api'
import { NotificationsLive } from '../features/notifications/NotificationsLive'
import { env } from '../config/env'
import { useListMemory } from './listMemory'

/**
 * Каркас админки: боковая панель (разделы, внизу — профиль и выход) и колонка
 * контента с заголовком раздела.
 * Пока вход не выполнен, вместо каркаса — экран входа (прототип прячет `#app`
 * целиком, чтобы под формой не мелькали данные). Временный пароль пускает
 * только на экран смены пароля: другие запросы сервер всё равно отклонит.
 */
export function AdminShell() {
  const { me, loading, mustChangePassword, can, signOut, authApi } = useSession()
  const { pathname } = useLocation()
  useListMemory()
  const [menuOpen, setMenuOpen] = useState(false)
  // Узел в строке заголовка, куда разделы кладут свои действия.
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  const [toast, setToast] = useState('')
  const hideToast = useCallback(() => {
    setToast('')
  }, [])
  // Метка у пункта «Заказы»: сколько заказов ждёт сборки. Считает сервер —
  // берём `total` страницы, а не длину списка, и сужать по аптеке не нужно:
  // сотруднику аптеки сервер и так отдаёт только её заказы. Строки не нужны —
  // страница в одну строку.
  const newOrders = useOrders({ status: 'new', pageSize: 1 }, can('orders'))
  // Уведомления — за флагом и правом на заказы; без входа их нет.
  const notifications = env.features.notifications && me !== null && can('notifications')
  const unread = useUnreadCount(notifications)

  // Сообщение показываем и без входа: «Вы вышли из системы» появляется уже
  // поверх формы входа.
  const corner = toast ? <Toast message={toast} onHide={hideToast} /> : null

  if (mustChangePassword) {
    return (
      <>
        <IconSprite />
        <PasswordScreen api={authApi} />
        {corner}
      </>
    )
  }

  if (loading) {
    return (
      <div className="adm-loading" role="status">
        Загружаем профиль…
      </div>
    )
  }

  if (!me) {
    return (
      <>
        <IconSprite />
        <LoginScreen api={authApi} />
        {corner}
      </>
    )
  }

  const module = currentModule(pathname)
  // Адрес вне разделов — это «страница не найдена», а не отказ в доступе:
  // проверять права нечему.
  const allowed = module ? can(module.id) : true
  // Карточка раздела (/products/30): заголовок с названием и возвратом рисует
  // она сама, повторять над ним «Товары» незачем.
  const detail = allowed && module !== null && pathname !== module.path

  return (
    <>
      <IconSprite />
      <div className="adm">
        <Sidebar
          open={menuOpen}
          me={me}
          newOrders={newOrders.data?.total ?? 0}
          unread={unread.data ?? 0}
          onNavigate={() => {
            setMenuOpen(false)
          }}
          onSignOut={() => {
            signOut()
            setToast('Вы вышли из системы')
          }}
        />
        <div
          className="side-ov"
          data-open={menuOpen}
          onClick={() => {
            setMenuOpen(false)
          }}
          aria-hidden="true"
        />
        <div className="adm-main">
          <div className="adm-content">
            <PageHeader
              title={
                detail
                  ? undefined
                  : module
                    ? allowed
                      ? module.name
                      : 'Нет доступа'
                    : 'Страница не найдена'
              }
              onBurger={() => {
                setMenuOpen(true)
              }}
              onSlot={setSlot}
            />
            <PageSlotContext.Provider value={slot}>
              {allowed ? (
                <Outlet context={{ toast: setToast }} />
              ) : (
                <div className="ui-card">
                  <div className="ui-empty">
                    <Icon name="i-key" />
                    <b>Роль «{me.roleName}» не открывает этот раздел</b>
                    <p>Доступ к разделам выдаёт супер-админ в разделе «Роли доступа».</p>
                  </div>
                </div>
              )}
            </PageSlotContext.Provider>
          </div>
        </div>
      </div>
      {notifications ? <NotificationsLive onToast={setToast} /> : null}
      {corner}
    </>
  )
}
